import React, { ReactNode, useContext, HTMLAttributes, useMemo } from 'react';
import { ThemeContext } from 'styled-components';

import DataFormat from '../../format/DataFormat';
import ColorSchemeContext from '../../colorScheme/ColorSchemeContext';
import COLOR_SCHEMES from '../../../constants/COLOR_SCHEMES';
import resolveKey from '../../../utils/components/_resolveKey';
import resolveStringValue from '../../../utils/components/_resolveStringValue';

import RadioItem from '../RadioItem';
import {
  StyledRadioListItemWrapper,
  StyledRadioList,
} from './RadioList.styles';
import RepeaterContext from '../_RepeaterContext';

export interface RadioListOption<Value> {
  label?: ReactNode;
  key?: string | string[];
  value: Value;
  /** Disables a single option. */
  disabled?: boolean;
}

export interface RadioListProps<Value>
  extends HTMLAttributes<HTMLInputElement> {
  /** The name of the radio list */
  name?: string;
  /** List of options in the list */
  options?: RadioListOption<Value>[];
  /** Display the list horizontally. */
  horizontal?: boolean;
  /** Removes the box around the list. */
  noBox?: boolean;
  /** Defines the current value of the `RadioList`.  */
  value: Value;
  /** @ignore */
  readOnly?: boolean;
  /** @ignore */
  textOnly?: boolean;
  /** @ignore */
  isValid?: boolean;
  /** @ignore */
  isTouched?: boolean;
  /** @ignore */
  disabled?: boolean;
  /** @ignore */
  hasWarning?: boolean;
  /** @ignore */
  hasCaution?: boolean;
  /** @ignore */
  hasInfo?: boolean;
}

const RadioListDefaultProps: Partial<RadioListProps<any>> = {
  options: [],
};

/**
 * @mode developer
 */
const RadioList = <Value extends Object | string>({
  options,
  value,
  horizontal,
  noBox,
  name,
  isValid,
  hasWarning,
  hasCaution,
  hasInfo,
  isTouched,
  disabled,
  readOnly,
  textOnly,
  ...props
}: RadioListProps<Value>) => {
  const { scheme } = useContext(ColorSchemeContext);
  const theme = useContext(ThemeContext);
  const resolvedHorizontal =
    typeof horizontal !== 'undefined'
      ? horizontal
      : theme?.RadioList?.horizontal;

  const resolvedNoBox = useMemo(() => {
    if (
      theme?.RadioList?.forceBoxOnStatus &&
      isTouched &&
      (hasWarning || hasCaution || hasInfo || !isValid)
    ) return false;
    return typeof noBox !== 'undefined' ? noBox : theme?.RadioList?.noBox;
  }, [
    isTouched,
    hasCaution,
    hasInfo,
    hasWarning,
    isValid,
    noBox,
    theme?.RadioList?.forceBoxOnStatus,
    theme?.RadioList?.noBox,
  ]);

  const { repeaterId, repeaterItemId } = useContext(RepeaterContext);

  if (textOnly) {
    const selectedOption = options.find((option) => option.value === value);
    return <DataFormat>{selectedOption?.label}</DataFormat>;
  }

  return (
    <StyledRadioList
      shaded={scheme === COLOR_SCHEMES.SHADE}
      data-testid="RadioList"
      data-test-name={name}
      horizontal={resolvedHorizontal}
      noBox={resolvedNoBox}
      isValid={isValid}
      hasWarning={hasWarning}
      hasCaution={hasCaution}
      hasInfo={hasInfo}
      isTouched={isTouched}
      disabled={disabled}
      readOnly={readOnly}
    >
      <StyledRadioListItemWrapper
        horizontal={resolvedHorizontal}
        noBox={resolvedNoBox}
        isValid={isValid}
        hasWarning={hasWarning}
        hasCaution={hasCaution}
        hasInfo={hasInfo}
        isTouched={isTouched}
      >
        {options.map(({ label, value: optionValue, key, disabled: optionDisabled }, index) => (
          <RadioItem
            {...props}
            name={repeaterId ? `${name}-${repeaterId}-${repeaterItemId}` : name}
            data-index={index}
            key={resolveKey(optionValue, key)}
            label={label}
            value={resolveStringValue(optionValue, key)}
            checked={
              resolveStringValue(value, key) ===
              resolveStringValue(optionValue, key)
            }
            horizontal={resolvedHorizontal}
            noBox={resolvedNoBox}
            disabled={typeof optionDisabled !== 'undefined' ? optionDisabled : disabled}
            readOnly={readOnly}
          />
        ))}
      </StyledRadioListItemWrapper>
    </StyledRadioList>
  );
};

RadioList.defaultProps = RadioListDefaultProps;

export default RadioList;
